import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, first, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { CreditScoreArrayGateway } from '@domain/models/credit-score-array/gateway/credit-score-array.gateway';
import { FeatureFlagsGateway } from '@domain/models/feature-flags/gateway/feature-flags.gateway';
import { ICreditScoreConfig, ICreditScoreStatus } from '@domain/interfaces/credit-score-array/credit-score-array.interface';
import { getStoredMemberToken } from '@infraestructure/helpers/auth/member-token.storage';

export const CREDIT_SCORE_FLAG = 'member-portal-credit-score';

export const INELIGIBLE_STATUS: ICreditScoreStatus = {
  eligible: false,
  viewState: 'Ineligible',
  estimateVisible: true,
};

/**
 * Single source of the member's Credit Score state for the page, the Home band, the menu and the
 * route guard. Anything that goes wrong (flag off, no member token, 401/403/404) resolves to
 * "ineligible", which is today's portal, unchanged.
 */
@Injectable({
  providedIn: 'root',
})
export class CreditScoreStateService {
  private readonly _status = new BehaviorSubject<ICreditScoreStatus | null>(null);
  private _loading$: Observable<ICreditScoreStatus> | null = null;
  private _config$: Observable<ICreditScoreConfig | null> | null = null;

  constructor(
    private _gateway: CreditScoreArrayGateway,
    private _featureFlags: FeatureFlagsGateway,
  ) {}

  /** Latest known status; loads it on first subscription. */
  get status$(): Observable<ICreditScoreStatus> {
    if (!this._status.value && !this._loading$) this.load().subscribe();
    return this._status.asObservable().pipe(filter((s): s is ICreditScoreStatus => s !== null));
  }

  get eligible$(): Observable<boolean> {
    return this.status$.pipe(
      first(),
      map((s) => s.eligible),
    );
  }

  get snapshot(): ICreditScoreStatus | null {
    return this._status.value;
  }

  /** Re-reads the status from the API and publishes it. */
  refresh(): Observable<ICreditScoreStatus> {
    this._loading$ = null;
    return this.load();
  }

  /** Publishes a status the API already returned (start/outcome responses carry one). */
  set(status: ICreditScoreStatus | undefined | null): void {
    if (status) this._status.next(status);
  }

  config(): Observable<ICreditScoreConfig | null> {
    if (!this._config$) {
      this._config$ = this._gateway.getConfig().pipe(
        catchError(() => of(null)),
        shareReplay(1),
      );
    }
    return this._config$;
  }

  reset(): void {
    this._loading$ = null;
    this._config$ = null;
    this._status.next(null);
  }

  private load(): Observable<ICreditScoreStatus> {
    if (this._loading$) return this._loading$;

    this._loading$ = this._featureFlags.isEnabled(CREDIT_SCORE_FLAG).pipe(
      first(),
      // An expired token is still sent: the API rejects it and the interceptor signs the member out.
      switchMap((enabled) => (enabled && getStoredMemberToken() ? this._gateway.getStatus() : of(INELIGIBLE_STATUS))),
      map((status) => (status?.eligible ? status : INELIGIBLE_STATUS)),
      catchError(() => of(INELIGIBLE_STATUS)),
      tap((status) => this._status.next(status)),
      shareReplay(1),
    );
    return this._loading$;
  }
}
